import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Calculator, FileText, TrendingDown, Shield } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TaxAdvisoryFiling() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Calculator, title: 'Tax Planning', description: 'Strategic tax planning to minimize your tax burden legally.' },
    { icon: FileText, title: 'Complete Filing', description: 'Accurate preparation and filing of all tax returns.' },
    { icon: TrendingDown, title: 'Maximize Deductions', description: 'Identify all eligible deductions and credits.' },
    { icon: Shield, title: 'IRS Compliance', description: 'Ensure full compliance with tax laws and regulations.' },
  ];

  const faqs = [
    { question: 'What tax advisory services do you provide?', answer: 'We provide tax planning, preparation and filing of business and personal returns, estimated tax calculations, tax strategy consulting, and IRS audit support.' },
    { question: 'When should I start tax planning?', answer: 'Tax planning should be year-round, not just at tax time. Strategic planning throughout the year can significantly reduce your tax burden.' },
    { question: 'What documents do I need for tax filing?', answer: 'Required documents include income statements (W-2, 1099s, K-1s), expense receipts, prior year returns, asset purchase information, and business financial statements.' },
    { question: 'Can you help with IRS audits?', answer: 'Yes, we provide audit representation and support, helping you respond to IRS inquiries and represent your interests throughout the audit process.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1554224311-beee460201f9?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Tax Advisory & Filing</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Professional tax planning and filing services to minimize your tax burden.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Tax Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Expert tax advisory and filing services for your business.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about our tax services.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to Optimize Your Taxes?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Get professional tax planning and filing services today.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="tax_advisory" />
      <Footer />
    </div>
  );
}